import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Breadcrumbs } from "../../AbstractElements";
import { Button, Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { toast } from "react-toastify";
import { ArrowLeft } from "react-feather";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import AsyncSelect from "react-select/async";
import { getItemDetails, getSaltOnSearch, updateItemInfo } from "../PharmacyMedicineAdd/PharmacyMedicineService";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";

// OTC product forms (sorted alphabetically)
export const otcProductForms = [
	{ id: 1, name: "Balm / Stick", value: "balm" },
	{ id: 2, name: "Biscuits / Snacks", value: "snack" },
	{ id: 3, name: "Capsule", value: "capsule" },
	{ id: 4, name: "Chewable Tablet", value: "chewable_tablet" },
	{ id: 5, name: "Cream", value: "cream" },
	{ id: 6, name: "Drops", value: "drops" },
	{ id: 7, name: "Drinks / Beverages", value: "drink" },
	{ id: 8, name: "Effervescent Tablet", value: "effervescent_tablet" },
	{ id: 9, name: "Film / Oral Film", value: "oral_film" },
	{ id: 10, name: "Gel", value: "gel" },
	{ id: 11, name: "Gummy / Chewable Bar", value: "gummy" },
	{ id: 12, name: "Lotion", value: "lotion" },
	{ id: 13, name: "Lozenge / Troche", value: "lozenge" },
	{ id: 14, name: "Medicated Soap / Bar", value: "medicated_soap" },
	{ id: 15, name: "Nebulizer Solution", value: "nebulizer_solution" },
	{ id: 16, name: "Patch", value: "patch" },
	{ id: 17, name: "Powder", value: "powder" },
	{ id: 18, name: "Powder for Infusion / Injection (reconstitution)", value: "powder_for_infusion" },
	{ id: 19, name: "Powder for Suspension", value: "powder_for_suspension" },
	{ id: 20, name: "Syrup / Oral Solution", value: "syrup" },
	{ id: 21, name: "Soft Gelatin Capsule", value: "soft_gelatin_capsule" },
	{ id: 22, name: "Spray / Inhaler", value: "spray" },
	{ id: 23, name: "Suppository", value: "suppository" },
	{ id: 24, name: "Tablet", value: "tablet" },
	{ id: 25, name: "Other", value: "other" }
]

// OTC package types (sorted alphabetically)
export const otcPackageTypes = [
	{ id: 1, name: "Ampoule / Vial", value: "ampoule" },
	{ id: 2, name: "Blister Pack", value: "blister_pack" },
	{ id: 3, name: "Bottle", value: "bottle" },
	{ id: 4, name: "Box / Carton", value: "box" },
	{ id: 5, name: "Dropper + Bottle Combo", value: "dropper_combo" },
	{ id: 6, name: "Dropper Bottle", value: "dropper_bottle" },
	{ id: 7, name: "Foil Pack / Foil Sachet", value: "foil_pack" },
	{ id: 8, name: "Jar", value: "jar" },
	{ id: 9, name: "Jar with Screw Cap", value: "jar_screw_cap" },
	{ id: 10, name: "Pump Bottle", value: "pump_bottle" },
	{ id: 11, name: "Package", value: "package" },
	{ id: 12, name: "Roll-on / Pen Dispenser", value: "roll_on" },
	{ id: 13, name: "Sachet / Pouch", value: "sachet" },
	{ id: 14, name: "Spray Bottle / Aerosol", value: "spray_bottle" },
	{ id: 15, name: "Stick Pack / Stick Tube", value: "stick_pack" },
	{ id: 16, name: "Tube", value: "tube" },
	{ id: 17, name: "Tube with Nozzle", value: "tube_nozzle" },
	{ id: 18, name: "Other", value: "other" }
]

const UpdateOTC = () => {
	const { mdcId } = useParams();
	const { t } = useTranslation();
	const history = useNavigate();
	const { setLoading } = useLoader();
	const { pharmacyPOS } = usePharmacy();
	const disableScrollProps = useDisableScrollOnFocus();

	// Track whether it's the first render
	const isFirstLoad = useRef(true);

	const [formData, setFormData] = useState({
		brandName: "",
		// saltName: "",
		manufacturer: "",
		package: "",
		productForm: "",
		quantity: "",
		regularPrice: "",
		sellingPrice: "",
		// discount: "",
		// tax: "",
		// taxType: "",
		note: "",
		// maxAlertQuantity: "",
		minAlertQuantity: "",
		// returnQuantity: "",
		// returnPrice: "",
		currency: ""
	});

	const [errors, setErrors] = useState({});

	// ✅ Fetch item details on load
	useEffect(() => {
		const fetchItem = async () => {
			try {
				setLoading(true);
				const body = {
					itemId: mdcId,
					clinicId: pharmacyPOS?.hospital_id,
				}

				const res = await getItemDetails(body);

				// ✅ Access the nested item
				if (res?.data?.data) {
					const item = res.data.data;

					setFormData({
						brandName: item.brandName || "",
						// saltName: item.saltName || "",
						manufacturer: item.manufacturer || "",
						package: item.package || "",
						productForm: item.productForm || "",
						quantity: item.packageQuantity || "",
						regularPrice: item.regularPrice || "",
						note: item.note || "",
						minAlertQuantity: item.minQuantityAlert || "",
						currency: item.currency || "USD",
					});
				}
			} catch (error) {
				console.error("Error fetching item:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchItem();
	}, [mdcId]);

	// ✅ Load medicines on search
	const loadMedicines = async (inputValue) => {
		if (!inputValue || inputValue.length < 3) {
			return [];
		}

		const body = {
			search: inputValue,
		};

		try {
			setLoading(true);
			const response = await getSaltOnSearch(body);
			const medicines = response.data.data.medication || [];

			return medicines.map((m) => ({
				value: m.medication_id,
				label: `${m.salt_composition}`,
				raw: m // keep full object for later use
			}));
		} catch (error) {
			console.error("Error fetching medicines:", error);
			return [];
		} finally {
			setLoading(false);
		}
	};

	const [searchQuery, setSearchQuery] = useState('');

	const handleMedicineSearchChange = (newValue) => {
		setSearchQuery(newValue);
	};

	// ✅ When a medicine is selected
	const handleSelectMedicine = (selectedOption) => {
		if (!selectedOption) {
			// clear saltName if user clears selection
			setFormData((prev) => ({
				...prev,
				saltName: "",
			}));
			return;
		}

		const medicine = selectedOption.raw;

		// Update form fields based on medicine data
		setFormData((prev) => ({
			...prev,
			saltName: medicine.salt_composition || "",
			// add more keys if needed
		}));
	};

	// ✅ Handle input change
	const handleChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	// ✅ Validation
	const validate = () => {
		let newErrors = {};
		if (!formData.brandName) newErrors.brandName = "Product name is required";
		// if (!formData.saltName) newErrors.saltName = "Salt name is required";
		if (!formData.manufacturer) newErrors.manufacturer = "Manufacturer is required";
		if (!formData.package) newErrors.package = "Package is required";
		if (!formData.productForm) newErrors.productForm = "Product form is required";
		if (!formData.quantity || formData.quantity <= 0) newErrors.quantity = "Quantity must be greater than 0";
		if (!formData.regularPrice || formData.regularPrice <= 0) newErrors.regularPrice = "Price must be greater than 0";
		// if (!formData.tax) newErrors.tax = "Tax is required";
		// if (!formData.taxType) newErrors.taxType = "Tax type is required";
		if (!formData.minAlertQuantity) newErrors.minAlertQuantity = "Minimum Alert quantity is required";
		// if (!formData.maxAlertQuantity) newErrors.maxAlertQuantity = "Maximum Alert quantity is required";
		if (!formData.currency) newErrors.currency = "Currency is required";
		return newErrors;
	};

	// ✅ Submit update
	const handleSubmit = async (e) => {
		e.preventDefault();
		const validationErrors = validate();
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}
		try {
			setLoading(true);

			const body = {
				itemId: mdcId,
				clinicId: pharmacyPOS?.hospital_id,
				brandName: formData.brandName,
				// saltName: formData.saltName,
				manufacturer: formData.manufacturer,
				package: formData.package,
				productForm: formData.productForm,
				packageQuantity: formData.quantity,
				regularPrice: formData.regularPrice,
				note: formData.note,
				minQuantityAlert: formData.minAlertQuantity,
				currency: formData.currency,
				isOTC: true
			}
			const response = await updateItemInfo(body)
			toast.success(response.data.message);
			gotoList();
		} catch (error) {
			console.error("Error updating item:", error);
		} finally {
			setLoading(false);
		}
	};

	const gotoList = () => {
		history(`${process.env.PUBLIC_URL}/items`);
	}

	return (
		<Fragment>
			<Breadcrumbs mainTitle={t("Update OTC Item")} parent={t("Dashboard")} title={t("Update OTC Item")} />
			<Row className="mt-3">
				<Col sm={12}>
					<Card>
						<CardHeader className="d-flex justify-content-end align-items-center">
							<button
								className="btn btn-primary d-flex align-items-center"
								style={{ color: 'white', height: "40px" }}
								to=""
								onClick={gotoList}>
								<ArrowLeft className="me-2" /> {t("Back")}
							</button>
						</CardHeader>
						<CardBody>
							<form onSubmit={handleSubmit} className="row g-3">

								{/* Product Name */}
								<div className="col-md-4">
									<label className="form-label">Product Name</label>
									<input
										type="text"
										name="brandName"
										value={formData.brandName}
										onChange={handleChange}
										className="form-control"
									/>
									{errors.brandName && <div className="text-danger">{errors.brandName}</div>}
								</div>

								{/* Manufacturer */}
								<div className="col-md-4">
									<label className="form-label">Manufacturer</label>
									<input
										type="text"
										name="manufacturer"
										value={formData.manufacturer}
										onChange={handleChange}
										className="form-control"
									/>
									{errors.manufacturer && <div className="text-danger">{errors.manufacturer}</div>}
								</div>

								{/* Package */}
								<div className="col-md-4">
									<label className="form-label">Package</label>
									<input
										type="text"
										name="package"
										value={formData.package}
										onChange={handleChange}
										className="form-control"
									/>
									{errors.package && <div className="text-danger">{errors.package}</div>}
								</div>
								{/* <div className="col-md-4">
									<label className="form-label">Package</label>
									<select
										name="package"
										value={formData.package}
										onChange={handleChange}
										className="form-select"
									>
										<option value="">Select Package</option>
										{otcPackageTypes.map((pkg, index) => (
											<option key={index} value={pkg.name}>
												{pkg.name}
											</option>
										))}
									</select>
									{errors.package && <div className="text-danger">{errors.package}</div>}
								</div> */}

								{/* Product Form */}
								<div className="col-md-4">
									<label className="form-label">Product Form</label>
									<input
										type="text"
										name="productForm"
										value={formData.productForm}
										onChange={handleChange}
										className="form-control"
									/>
									{errors.productForm && <div className="text-danger">{errors.productForm}</div>}
								</div>
								{/* <div className="col-md-4">
									<label className="form-label">Product Form</label>
									<select
										name="productForm"
										value={formData.productForm}
										onChange={handleChange}
										className="form-select"
									>
										<option value="">Select Product Form</option>
										{otcProductForms.map((form, index) => (
											<option key={index} value={form.name}>
												{form.name}
											</option>
										))}
									</select>
									{errors.productForm && <div className="text-danger">{errors.productForm}</div>}
								</div> */}

								{/* Quantity */}
								<div className="col-md-4">
									<label className="form-label">{t("Product Quantity")}</label>
									<input
										type="number"
										name="quantity"
										min={1}
										value={formData.quantity}
										onChange={handleChange}
										className="form-control"
										{...disableScrollProps}
									/>
									{errors.quantity && <div className="text-danger">{errors.quantity}</div>}
								</div>

								{/* MRP */}
								<div className="col-md-4">
									<label className="form-label">MRP</label>
									<input
										type="number"
										name="regularPrice"
										value={formData.regularPrice}
										onChange={handleChange}
										className="form-control"
										{...disableScrollProps}
									/>
									{errors.regularPrice && <div className="text-danger">{errors.regularPrice}</div>}
								</div>

								{/* Currency */}
								<div className="col-md-4">
									<label className="form-label">Currency</label>
									<select
										name="currency"
										value={formData.currency}
										onChange={handleChange}
										className="form-select"
										disabled
									>
										{/* <option value="" disabled>Select Currency</option> */}
										<option value="INR">INR</option>
										{/* <option value="ZiG">ZiG</option> */}
									</select>
									{errors.currency && <div className="text-danger">{errors.currency}</div>}
								</div>

								{/* Alert Quantity */}
								<div className="col-md-3">
									<label className="form-label">Minimum Alert Quantity</label>
									<input
										type="number"
										name="minAlertQuantity"
										min={0}
										value={formData.minAlertQuantity}
										onChange={handleChange}
										className="form-control"
										{...disableScrollProps}
									/>
									{errors.minAlertQuantity && <div className="text-danger">{errors.minAlertQuantity}</div>}
								</div>

								{/* Note */}
								<div className="col-md-12">
									<label className="form-label">Note</label>
									<textarea
										name="note"
										value={formData.note}
										onChange={handleChange}
										className="form-control"
									/>
								</div>

								{/* Buttons */}
								<div className="d-flex justify-content-end gap-2">
									<button className="btn btn-primary" type="submit">
										{t("Update Item")}
									</button>
									<Button
										variant="secondary"
										onClick={gotoList}
									>
										{t("Cancel")}
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Col>
			</Row>
		</Fragment>
	);
};

export default UpdateOTC;
